import { nextTick } from 'vue';
import {
  isFeedScrollLayoutSettling,
  getPendingFeedScrollRestore,
} from '@/composables/useFeedScrollContext.js';
import { FEED_VIEWPORT_FILL_MAX_DEPTH } from '@/constants/feed-pagination.js';
import { MOBILE_MAX_PX } from '@/constants/layout-breakpoints.js';

/**
 * 虚拟列表 remeasure 期间 scrollHeight 可能暂时偏小，勿据此 loadMore。
 * 仅用于视口填充；scroll 触底不要用 overflow 条件（会误拦正常上滑加载）。
 */
export function shouldIgnoreFeedScrollLoad(container) {
  if (!container) return true;
  if (isFeedScrollLayoutSettling()) return true;
  if (getPendingFeedScrollRestore() > 0) return true;
  return false;
}

/** 视口填充专用：scrollHeight 暂不稳定时跳过（勿用于 scroll 触底） */
export function shouldIgnoreFeedViewportFill(container) {
  if (shouldIgnoreFeedScrollLoad(container)) return true;
  const { scrollTop, scrollHeight, clientHeight } = container;
  if (scrollTop > 0 && scrollTop + clientHeight > scrollHeight + 8) return true;
  return false;
}

/** DOM 更新后等 layout/paint 稳定再读 scrollHeight，避免误判「填不满」 */
export function waitForScrollLayoutStable() {
  return new Promise((resolve) => {
    void nextTick().then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  });
}

export function scrollerCanScroll(container) {
  if (!container) return false;
  return container.scrollHeight > container.clientHeight + 4;
}

export function isH5FeedViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches;
}

export function feedScrollerIsActive(scroller) {
  if (!scroller) return false;
  return scrollerCanScroll(scroller);
}

/**
 * H5 若 Feed scroller 未产生内滚（body 在滚），回退到 document 度量触底。
 */
export function resolveFeedScrollContainer(scroller) {
  if (!scroller) return null;
  if (feedScrollerIsActive(scroller)) return scroller;
  if (!isH5FeedViewport() || typeof document === 'undefined') return scroller;
  const docEl = document.documentElement;
  if (docEl.scrollHeight > window.innerHeight + 4) return docEl;
  return scroller;
}

/** 统一 scroller / document 滚动度量，供 paginatedFeedOnContainerScroll 使用 */
export function getFeedScrollMetrics(container) {
  if (!container || typeof window === 'undefined') return null;
  const docEl = document.documentElement;
  if (container === docEl || container === document.body) {
    return {
      scrollTop: window.scrollY || docEl.scrollTop || 0,
      clientHeight: window.innerHeight,
      scrollHeight: docEl.scrollHeight,
    };
  }
  return {
    scrollTop: container.scrollTop,
    clientHeight: container.clientHeight,
    scrollHeight: container.scrollHeight,
  };
}

/**
 * 首屏高度不足时自动 loadMore，直到可滚动或无更多数据。
 * 若用户已滚动（scrollTop > 0）则跳过，避免 keep-alive 返回时误触发。
 */
export async function ensureFeedViewportFilled(store, getScrollElement, depth = 0) {
  if (depth >= FEED_VIEWPORT_FILL_MAX_DEPTH) return;
  if (store.listLoading || store.loadingMore || !store.hasMore || !store.rawRows?.length) return;

  await waitForScrollLayoutStable();

  const el = getScrollElement();
  if (!el) return;
  if (shouldIgnoreFeedViewportFill(el)) return;
  if (getPendingFeedScrollRestore() > 0) return;
  if (el.scrollTop > 0) return;
  if (scrollerCanScroll(el)) return;

  await store.loadMore({ viewportFill: true });
  await ensureFeedViewportFilled(store, getScrollElement, depth + 1);
}
